import { Link, useParams } from 'react-router';
import { useEffect, useRef } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { QuickActionsNav } from '@/components/quick-actions-nav';
import { MyProfileAvatar } from '@/components/my-profile-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { PlantControlBreadcrumbs } from '@/features/plants/components/plant-control/plant-control-breadcrumbs.tsx';
import { PlantControlScripts } from '@/features/plants/components/plant-control/plant-control-scripts.tsx';
import { PlantControlChart } from '@/features/plants/components/plant-control/plant-control-chart.tsx';
import PlantControlPowerFlow from '@/features/plants/components/plant-control/plant-control-power-flow.tsx';
import { usePostTriggerPlantDataTransmission } from '@/features/plants/hooks/mutations/[plantId]/use-post-trigger-plant-data-transmission.ts';
import PlantUptimeStatusPopover from '@/features/plants/components/plant-monitoring/plant-uptime-status-popover.tsx';
import { PlantMenuPopover } from '@/features/plants/components/plant-overview/plant-menu-popover';

const MINUTE_IN_SECONDS = 60 * 1000;
const MINUTES = 4;

export const ControlPanelPlant = () => {
  const { plantId } = useParams();
  const isMobile = useMediaQuery('(max-width:640px)');
  const hasFired = useRef(false);

  const postTriggerPlantDataTransmission = usePostTriggerPlantDataTransmission({});

  useEffect(() => {
    if (!plantId) return;

    if (postTriggerPlantDataTransmission.isIdle && !hasFired.current) {
      hasFired.current = true;
      postTriggerPlantDataTransmission.mutate({ plantId });
    }
    const timer = setInterval(() => {
      postTriggerPlantDataTransmission.mutate({ plantId });
    }, MINUTE_IN_SECONDS * MINUTES);

    return () => clearInterval(timer);
  }, []);

  const { data: plant, isLoading: isPlantLoading } = useGetPlantQuery({
    plantId: plantId || '',
  });

  return (
    <div className="overview w-full gap-2 overflow-auto px-4 pt-3 pb-8 sm:px-6">
      <div className="flex flex-col gap-6">
        <div className="flex h-[45px] items-center justify-between">
          {!isMobile ? (
            <PlantControlBreadcrumbs
              plantName={plant?.basicDetails.name || ''}
              plantId={plantId || ''}
              isMobile={isMobile}
            />
          ) : (
            <Button variant="outline" asChild className="size-8 p-0">
              <Link to="/plants">
                <ChevronLeft />
              </Link>
            </Button>
          )}

          <div className="flex gap-2">
            <QuickActionsNav />
            <MyProfileAvatar />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {isPlantLoading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <div className="flex w-full justify-between">
              <h2 className="text-2xl font-semibold">{plant?.basicDetails.name}</h2>
              <div className="flex items-center gap-2">
                <PlantUptimeStatusPopover />
                <PlantMenuPopover isLoading={isPlantLoading} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
            <PlantControlScripts className="col-span-full row-start-1 h-full min-h-[270px] sm:min-h-[400px] xl:col-start-1 xl:col-end-4 xl:row-start-1 xl:row-end-2 xl:min-h-[470px]" />
            <PlantControlPowerFlow className="col-span-full row-start-2 min-h-[470px] xl:col-start-4 xl:col-end-6 xl:row-start-1 xl:row-end-2" />
            <PlantControlChart className="col-span-full row-start-3 min-h-[488px] xl:col-start-1 xl:col-end-6 xl:row-start-2 xl:row-end-3" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControlPanelPlant;
